import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Avatar,
  Checkbox,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  SxProps,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Search as SearchIcon } from '@mui/icons-material';

import { useAppSelector } from '../../../../../hooks';
import { getShortName } from '../../../../../helper';
import { getCourseStudentsState } from '../../../../../store/V2/CourseStudentsSlice';
import { getCourseTutorsState } from '../../../../../store/V2/CourseTutorsSlice';
import { TP_COLORS } from '../../../../../constants';
import { TPTextField } from '../../../../../components/Inputs';
import {
  adminChatStartNewConversation,
  getAdminChatState,
} from '../../../../../store/V2/AdminChatSlice';

type TSelectOption = {
  label: string;
  value: string;
  avatar?: string;
  role: string;
};

export const avatarStyles: SxProps = {
  width: '35px',
  height: '35px',
};

const filterUserOption = (option: TSelectOption, searchText: string) => {
  return option.label.toLowerCase().includes(searchText.toLowerCase());
};

const AdminChatNewConversationView: FunctionComponent = () => {
  const urlParams = useParams();
  const dispatch = useDispatch();

  // Store
  const courseStudentsState = useAppSelector(getCourseStudentsState);
  const courseTutorsState = useAppSelector(getCourseTutorsState);
  const adminChatState = useAppSelector(getAdminChatState);

  // State
  const [searchText, setSearchText] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<TSelectOption[]>([]);
  const [userOptions, setUserOptions] = useState<TSelectOption[]>([]);

  const { adminChatConversationsData } = adminChatState;

  const urlCourseId = urlParams.id;
  const userOptionsList = [...userOptions].filter((option) =>
    filterUserOption(option, searchText)
  );

  useEffect(() => {
    const newUserOptions: TSelectOption[] = [];

    if (
      courseStudentsState &&
      courseStudentsState.adminCourseStudentsData &&
      courseStudentsState.adminCourseStudentsData.list
    ) {
      const _courseStudents =
        courseStudentsState.adminCourseStudentsData.list || [];

      _courseStudents.forEach((courseStudent) => {
        const _student = courseStudent.student;
        const isAlreadyExists = newUserOptions.some(
          (item) => item.value === _student._id
        );

        if (!isAlreadyExists) {
          newUserOptions.push({
            label: getShortName(_student.firstname, _student.lastname, true),
            value: _student._id,
            avatar: _student.avatar,
            role: _student.role.name,
          });
        }
      });
    }

    if (
      courseTutorsState &&
      courseTutorsState.adminCourseTutorsData &&
      courseTutorsState.adminCourseTutorsData.list
    ) {
      const _courseTutors = courseTutorsState.adminCourseTutorsData.list || [];

      _courseTutors.forEach((courseTutor) => {
        const _tutor = courseTutor.tutor;
        const isAlreadyExists = newUserOptions.some(
          (item) => item.value === _tutor._id
        );

        if (!isAlreadyExists) {
          newUserOptions.push({
            label: getShortName(_tutor.firstname, _tutor.lastname, true),
            value: _tutor._id,
            avatar: _tutor.avatar,
            role: _tutor.role.name,
          });
        }
      });
    }

    setUserOptions(newUserOptions);
  }, []);

  const handleUserCheck = (option: TSelectOption) => {
    setSelectedUsers((prevState) => {
      let _newState = [...prevState];
      const _isExist = _newState.some((item) => item.value === option.value);

      if (_isExist) {
        _newState = _newState.filter((item) => item.value !== option.value);
      } else {
        _newState.push({ ...option });
      }

      return _newState;
    });
  };

  const startConversation = () => {
    if (!urlCourseId || selectedUsers.length === 0) return;

    dispatch(
      adminChatStartNewConversation({
        courseId: urlCourseId,
        userIds: selectedUsers.map((item) => item.value),
      })
    );
  };

  return (
    <Stack height="100%" justifyContent="space-between">
      <div>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            backgroundColor: TP_COLORS.listItemGray.light,
            borderRadius: '28px',
            padding: '8px',
            margin: '8px',
          }}
        >
          <SearchIcon
            fontSize="small"
            sx={{ padding: '5px', color: TP_COLORS.gray.darken }}
          />
          <TPTextField
            multiline
            fullWidth
            placeholder="Search"
            variant="standard"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '14px',
              },
            }}
          />
        </Stack>
        <List sx={{ overflowY: 'auto' }}>
          {userOptionsList.map((userOption) => {
            const isSelected = selectedUsers.some(
              (item) => item.value === userOption.value
            );

            return (
              <ListItemButton
                key={userOption.value}
                onClick={() => handleUserCheck(userOption)}
              >
                <ListItemAvatar sx={{ mt: 0 }}>
                  <Avatar
                    alt={userOption.label}
                    src={userOption.avatar}
                    sx={avatarStyles}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={userOption.label}
                  secondary={userOption.role}
                  className="ellipsis-text two-lines"
                />
                <Checkbox edge="end" checked={isSelected} disableRipple />
              </ListItemButton>
            );
          })}
        </List>
      </div>
      <Stack alignItems="center">
        <LoadingButton
          loading={adminChatConversationsData.isCreating}
          disabled={!selectedUsers.length}
          onClick={startConversation}
          sx={{
            m: 2,
            fontWeight: 600,
            backgroundColor: TP_COLORS.border.color1,
            color: TP_COLORS.gray.darken,
          }}
        >
          Start Chat
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

export default AdminChatNewConversationView;
